

export type TutorialHintState = {
  version: 1;
  toolKey: string;
  firstSeenAt: number;
  lastOpenedAt?: number;
  lastClosedAt?: number;
  lastDurationMs?: number;
  dismissedCount: number;
  lastDismissedAt?: number;
  watchedCount: number;
  lastWatchedAt?: number;
  snoozeUntil?: number;
};

export type EmphasisLevel = "pulse" | "glow" | "none";


const DISMISS_MS = 8000;
const WATCH_MS = 30000;
const SNOOZE_MS = 24 * 60 * 60 * 1000;
const MAX_DISMISSALS = 2;

const STORAGE_KEY_PREFIX = "tutorial_hint_";


function safeGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}


export function readTutorialHintState(toolKey: string): TutorialHintState | null {
  const raw = safeGet(`${STORAGE_KEY_PREFIX}${toolKey}`);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as TutorialHintState;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeTutorialHintState(state: TutorialHintState): boolean {
  return safeSet(`${STORAGE_KEY_PREFIX}${state.toolKey}`, JSON.stringify(state));
}

export function getOrCreateTutorialHintState(toolKey: string): TutorialHintState {
  const existing = readTutorialHintState(toolKey);
  if (existing) return existing;

  const initial: TutorialHintState = {
    version: 1,
    toolKey,
    firstSeenAt: Date.now(),
    dismissedCount: 0,
    watchedCount: 0,
  };

  writeTutorialHintState(initial);
  return initial;
}

export function computeEmphasisLevel(state: TutorialHintState | null): EmphasisLevel {
  if (!state) return "pulse";

  if (state.watchedCount > 0) return "none";

  if (state.dismissedCount >= MAX_DISMISSALS) return "none";

  if (state.snoozeUntil && Date.now() < state.snoozeUntil) return "none";

  if (!state.lastOpenedAt) return "pulse";

  if (state.dismissedCount > 0) return "glow";

  return "pulse";
}

function classifyCloseAction(durationMs: number): "dismissed" | "watched" | "neutral" {
  if (durationMs < DISMISS_MS) return "dismissed";
  if (durationMs >= WATCH_MS) return "watched";
  return "neutral";
}

export function updateStateOnOpen(toolKey: string): TutorialHintState {
  const state = getOrCreateTutorialHintState(toolKey);
  const updated: TutorialHintState = {
    ...state,
    lastOpenedAt: Date.now(),
  };
  writeTutorialHintState(updated);
  return updated;
}

export function updateStateOnClose(
  toolKey: string,
  durationMs: number
): { state: TutorialHintState; action: "dismissed" | "watched" | "neutral" } {
  const state = getOrCreateTutorialHintState(toolKey);
  const action = classifyCloseAction(durationMs);
  const now = Date.now();

  const updated: TutorialHintState = {
    ...state,
    lastClosedAt: now,
    lastDurationMs: durationMs,
  };

  if (action === "dismissed") {
    updated.dismissedCount = (state.dismissedCount || 0) + 1;
    updated.lastDismissedAt = now;
    if (updated.dismissedCount < MAX_DISMISSALS) {
      updated.snoozeUntil = now + SNOOZE_MS;
    }
  } else if (action === "watched") {
    updated.watchedCount = (state.watchedCount || 0) + 1;
    updated.lastWatchedAt = now;
    updated.snoozeUntil = undefined;
  }

  writeTutorialHintState(updated);
  return { state: updated, action };
}

export function createToolKey(toolId: string | undefined, toolLabel: string): string {
  if (toolId) return toolId;
  return toolLabel
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}
